import { ReactNode } from 'react'
import { CircleCheckBig } from 'lucide-react'
import { Button } from '@/common/ui/button'

type FormLayoutProps = {
    title: string
    description: string
    children: ReactNode
    onCancel: () => void
    onSave: () => void
    isLoading: boolean
    isValid: boolean
    cancelLabel: string
    saveLabel: string
}

type SuccessLayoutProps = {
    title: string
    onClose: () => void
    closeLabel: string
}

export function AccountSettingsFormLayout({
    title,
    description,
    children,
    onCancel,
    onSave,
    isLoading,
    isValid,
    cancelLabel,
    saveLabel,
}: FormLayoutProps) {
    return (
        <section className="self-stretch w-full flex flex-col justify-center items-center gap-6 pb-36 md:pb-0 px-6 md:px-0">
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                    <h1 className="flex-1 text-black text-4xl font-normal font-['Tangerine'] leading-10">{title}</h1>
                </div>

                <p className="self-stretch text-black text-base font-normal leading-6">{description}</p>

                {children}
            </div>

            <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-6 bg-white px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] md:static md:z-auto md:w-full md:justify-between md:gap-6 md:bg-transparent md:px-0 md:pt-8 md:pb-0 md:shadow-none">
                <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={onCancel}
                    disabled={isLoading}
                    className="flex-1 md:flex-none md:w-28"
                >
                    {cancelLabel}
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    size="lg"
                    disabled={!isValid || isLoading}
                    onClick={onSave}
                    className="flex-1 md:flex-none md:w-28"
                >
                    {isLoading ? 'Saving...' : saveLabel}
                </Button>
            </div>
        </section>
    )
}

export function AccountSettingsSuccessLayout({ title, onClose, closeLabel }: SuccessLayoutProps) {
    return (
        <section className="self-stretch w-full flex flex-col justify-start items-start gap-12 pb-36 md:pb-0 px-6 md:px-0">
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
                <CircleCheckBig className="size-10 text-primary-light" />
                <div className="self-stretch flex flex-col justify-start items-start gap-6">
                    <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                        <h1 className="flex-1 text-black text-4xl font-normal font-['Tangerine'] leading-10">
                            {title}
                        </h1>
                    </div>
                </div>
            </div>

            <div className="fixed inset-x-0 bottom-0 z-40 flex items-center justify-between gap-6 bg-white px-6 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)] md:static md:z-auto md:w-full md:justify-start md:gap-6 md:bg-transparent md:px-0 md:pt-10 md:pb-0 md:shadow-none">
                <Button variant="secondary" type="button" size="lg" onClick={onClose} className="flex-1 md:flex-none">
                    {closeLabel}
                </Button>
            </div>
        </section>
    )
}
